import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HiMenu, HiX, HiChevronDown } from 'react-icons/hi'
import { getLanguageFromPath, getPathWithoutLanguage, createLocalizedPath } from '../../utils/languageUtils'
import { saveLanguageChoice } from '../../utils/geoLanguage'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import './Header.css'

function Header() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const languageMenuRef = useRef(null)
  const dropdownRefs = useRef({})

  const languages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'ru', name: 'Russian', nativeName: 'Русский' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語' }
  ]

  // Синхронизация языка с URL путем
  useEffect(() => {
    const langFromPath = getLanguageFromPath(location.pathname)
    // Обновляем язык только если он отличается и валиден
    // Проверяем, что язык из пути соответствует валидным языкам
    if (langFromPath !== i18n.language && ['en', 'ru', 'ja'].includes(langFromPath)) {
      i18n.changeLanguage(langFromPath)
    }
  }, [location.pathname, i18n]) // Включаем i18n для синхронизации, но проверка внутри предотвращает циклы

  // Закрытие меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setIsLanguageMenuOpen(false)
      }
      
      // Закрытие выпадающих меню
      let clickedOutside = true
      Object.values(dropdownRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) {
          clickedOutside = false
        }
      })
      
      if (clickedOutside) {
        setOpenDropdown(null)
      }
    }

    if (isLanguageMenuOpen || openDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isLanguageMenuOpen, openDropdown])

  const handleLanguageChange = (langCode) => {
    saveLanguageChoice(langCode)
    // Получаем текущий путь без языкового префикса
    const pathWithoutLang = getPathWithoutLanguage(location.pathname)
    // Создаем новый путь с выбранным языком
    const newPath = createLocalizedPath(pathWithoutLang, langCode)
    // Сохраняем query параметры и hash
    const search = location.search
    const hash = location.hash
    // Переходим на новый путь с сохранением параметров
    navigate(`${newPath}${search}${hash}`)
    setIsLanguageMenuOpen(false)
  }

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0]
  const getLocalizedLink = useLocalizedLink()

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    // Блокируем прокрутку body при открытом меню
    if (!isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setIsLanguageMenuOpen(false)
    setOpenDropdown(null)
    document.body.style.overflow = ''
  }

  const toggleDropdown = (menuName) => {
    setOpenDropdown(openDropdown === menuName ? null : menuName)
  }

  const isActive = (path) => {
    const pathWithoutLang = getPathWithoutLanguage(location.pathname)
    // Точное совпадение
    if (pathWithoutLang === path || pathWithoutLang === `${path}/`) {
      return true
    }
    // Проверка вложенных путей (только если текущий путь начинается с path + /)
    // Это нужно для случаев, когда path = '/about', а текущий путь = '/about/something'
    if (pathWithoutLang.startsWith(`${path}/`)) {
      return true
    }
    return false
  }

  const isParentActive = (paths) => {
    return paths.some(path => isActive(path))
  }

  // Структура меню с подменю (оптимизированный порядок)
  const menuItems = [
    {
      key: 'home',
      label: t('common.home'),
      path: '/',
      hasDropdown: false
    },
    {
      key: 'news',
      label: t('common.news'),
      path: '/news',
      hasDropdown: false
    },
    {
      key: 'videos',
      label: t('common.videos'),
      path: '/videos',
      hasDropdown: false
    },
    {
      key: 'about',
      label: t('common.aboutSUMA', 'О SUMA'),
      path: '/about',
      hasDropdown: true,
      submenu: [
        { label: t('common.about'), path: '/about' },
        { label: t('common.founder'), path: '/founder' },
        { label: t('common.president'), path: '/president' },
        { label: t('common.presidium'), path: '/presidium' },
        { label: t('common.representatives'), path: '/representatives' }
      ]
    },
    {
      key: 'martialArts',
      label: t('common.martialArts'),
      path: '/martial-arts',
      hasDropdown: true,
      submenu: [
        { label: t('common.disciplines', 'Направления'), path: '/martial-arts' },
        { label: t('common.certifications'), path: '/certifications' },
        { label: t('common.blackBelts'), path: '/black-belts' },
        { label: t('common.coloredBelts'), path: '/colored-belts' }
      ]
    },
    {
      key: 'organizations',
      label: t('common.organizations', 'Организации'),
      path: '/schools',
      hasDropdown: true,
      submenu: [
        { label: t('common.schools'), path: '/schools' },
        { label: t('common.federations'), path: '/federations' }
      ]
    },
    {
      key: 'events',
      label: t('common.events'),
      path: '/events',
      hasDropdown: true,
      submenu: [
        { label: t('common.eventsCalendar', 'Календарь событий'), path: '/events' },
        { label: t('common.competitionRules'), path: '/competition-rules' },
        { label: t('common.championsLeague'), path: '/champions-league' },
        { label: t('common.recordBook'), path: '/record-book' },
        { label: t('common.honoraryAwards'), path: '/honorary-awards' }
      ]
    },
    {
      key: 'membership',
      label: t('common.membership'),
      path: '/membership',
      hasDropdown: true,
      submenu: [
        { label: t('common.aboutMembership', 'О членстве'), path: '/membership' },
        { label: t('common.join', 'Вступить'), path: '/join' }
      ]
    },
    {
      key: 'resources',
      label: t('common.resources', 'Ресурсы'),
      path: '/documents',
      hasDropdown: true,
      submenu: [
        { label: t('common.documents'), path: '/documents' },
        { label: t('common.videos'), path: '/videos' },
        { label: t('documents.charterPage.navLabel'), path: '/documents/charter' },
        { label: t('documents.attestationGuidelinesPage.navLabel'), path: '/documents/attestation-guidelines' },
        { label: t('common.books'), path: '/books' }
      ]
    },
    {
      key: 'contact',
      label: t('common.contact'),
      path: '/contact',
      hasDropdown: false
    }
  ]

  return (
    <header className="header">
      <div className="header-container">
        <Link to={getLocalizedLink('/')} className="header-logo" onClick={closeMobileMenu}>
          <img src="/logo-isuma.png" alt="SUMA International" className="logo-image" />
          <div className="logo-text-container">
            <span className="logo-text">SUMA</span>
            <span className="logo-subtitle">International</span>
          </div>
        </Link>

        <nav className={`header-nav ${isMobileMenuOpen ? 'nav-open' : ''}`}>
          {menuItems.map(item => {
            if (!item.hasDropdown) {
              return (
                <Link 
                  key={item.key}
                  to={getLocalizedLink(item.path)} 
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  {item.label}
                </Link>
              )
            } else {
              const isOpen = openDropdown === item.key
              const isItemActive = item.path ? isActive(item.path) : isParentActive(item.submenu.map(s => s.path))
              
              return (
                <div 
                  key={item.key}
                  className={`nav-item-dropdown ${isItemActive ? 'active' : ''}`}
                  ref={el => dropdownRefs.current[item.key] = el}
                >
                  {item.path ? (
                    <div className="nav-link-wrapper">
                      <Link 
                        to={getLocalizedLink(item.path)}
                        className={`nav-link nav-link-with-dropdown ${isItemActive ? 'active' : ''}`}
                        onClick={(e) => {
                          const isDesktop = window.innerWidth > 1023
                          if (isDesktop) {
                            e.preventDefault()
                            toggleDropdown(item.key)
                          } else {
                            // На мобильных - просто переходим по ссылке, меню закрывается
                            closeMobileMenu()
                          }
                        }}
                      >
                        {item.label}
                        <HiChevronDown className={`dropdown-chevron dropdown-chevron-desktop ${isOpen ? 'open' : ''}`} />
                      </Link>
                      <button
                        className="nav-dropdown-toggle-mobile"
                        onClick={() => toggleDropdown(item.key)}
                        aria-label="Toggle submenu"
                      >
                        <HiChevronDown className={`dropdown-chevron ${isOpen ? 'open' : ''}`} />
                      </button>
                    </div>
                  ) : (
                    <button
                      className={`nav-link nav-link-with-dropdown ${isItemActive ? 'active' : ''}`}
                      onClick={() => toggleDropdown(item.key)}
                    >
                      {item.label}
                      <HiChevronDown className={`dropdown-chevron ${isOpen ? 'open' : ''}`} />
                    </button>
                  )}
                  {isOpen && (
                    <div className="nav-dropdown">
                      {item.submenu.map((subItem, idx) => {
                        // Проверяем активность подпункта более точно
                        const pathWithoutLang = getPathWithoutLanguage(location.pathname)
                        // subItem.path уже без языкового префикса (например, '/about')
                        const subItemPath = subItem.path
                        
                        // Нормализуем пути (убираем trailing slash и приводим к единому виду)
                        const normalizePath = (path) => {
                          if (!path || path === '/') return '/'
                          return path.replace(/\/$/, '')
                        }
                        
                        const currentPath = normalizePath(pathWithoutLang)
                        const normalizedSubPath = normalizePath(subItemPath)
                        const parentPath = item.path ? normalizePath(item.path) : null
                        
                        // Активным считается только точное совпадение пути
                        // И только если путь подпункта отличается от пути родителя
                        // Это предотвращает выделение первого подпункта, когда он совпадает с родителем
                        const isSubItemActive = currentPath === normalizedSubPath && 
                                               normalizedSubPath !== parentPath
                        
                        return (
                          <Link
                            key={idx}
                            to={getLocalizedLink(subItem.path)}
                            className={`nav-dropdown-item ${isSubItemActive ? 'active' : ''}`}
                            onClick={closeMobileMenu}
                          >
                            {subItem.label}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            }
          })}
        </nav>

        <div className="header-actions">
          <div className="language-selector" ref={languageMenuRef}>
            <button 
              className="language-toggle"
              onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
              aria-label={t('common.language')}
              aria-expanded={isLanguageMenuOpen}
              title={t('common.language')}
            >
              <span className="language-code">{currentLanguage.code.toUpperCase()}</span>
              <HiChevronDown className={`language-chevron ${isLanguageMenuOpen ? 'open' : ''}`} />
            </button>
            {isLanguageMenuOpen && (
              <div className="language-dropdown">
                {languages.map(lang => (
                  <button
                    key={lang.code}
                    className={`language-option ${i18n.language === lang.code ? 'active' : ''}`}
                    onClick={() => handleLanguageChange(lang.code)}
                    aria-label={t('common.switchToLanguage', { language: lang.nativeName }, `Switch to ${lang.nativeName}`)}
                  >
                    <span className="language-code-small">{lang.code.toUpperCase()}</span>
                    <span className="language-name">{lang.nativeName}</span>
                    {i18n.language === lang.code && (
                      <span className="language-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label={t('common.toggleMenu', 'Toggle menu')}
          >
            {isMobileMenuOpen ? <HiX /> : <HiMenu />}
          </button>
        </div>
      </div>
      <div className="header-divider"></div>
    </header>
  )
}

export default Header

